import React from "react";
import {
  Modal,
  View,
  Text,
  Pressable,
  StyleSheet,
  Dimensions,
  Platform,
  TouchableOpacity,
} from "react-native";
// SK-VOICE-FE uses `react-native-vector-icons/Ionicons` for the close glyph.
// SKCatalog ships its own SVG Icon, so "close" maps to "x" — every other line
// is identical to the source component.
import Ionicons from "./icon";
import { FontFamily } from "../theme/typography";
import { useTheme } from "../context/themeContext";


/** * Screen dimensions used for responsive layout calculations.
 */
const { width } = Dimensions.get("window");

/* ================= PROPS ================= */

/**
 * Interface defining properties for the ConfirmDialog component.
 * Supports flexible styling for both generic and destructive (e.g., delete) actions.
 */
type ConfirmDialogProps = Readonly<{
  visible: boolean;          // Controls the visibility of the modal
  title?: string;            // Primary heading text
  message: string;           // Descriptive text explaining the action consequences
  onConfirm: () => void;     // Callback executed upon positive action
  onCancel: () => void;      // Callback executed to dismiss the dialog without changes
  confirmText?: string;      // Custom label for the confirm button
  cancelText?: string;       // Custom label for the cancel button
  confirmColor?: string;     // Themed color for the confirm button and header indicator
  busy?: boolean;            // Disables buttons + shows a "Working…" label during async work
  onClose?: () => void;      // Optional callback for a top-right close icon
  children?: React.ReactNode; // Optional custom content (e.g., input fields)
}>;

/* ================= COMPONENT ================= */

/**
 * A reusable confirmation dialog utilizing a Modal with fade animations.
 * Designed with high-end UI patterns including squircle buttons and soft overlays.
 */
export default function ConfirmDialog({
  visible,
  title = "Confirm Action",
  message,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor, // Themed; defaults to danger below
  busy = false,
  onClose,
  children,
}: ConfirmDialogProps) {
  const { theme } = useTheme();
  const { colors } = theme;
  const accentColor = confirmColor ?? colors.danger; // Destructive default
  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      // Handles hardware back button interaction on Android
      onRequestClose={onCancel}
    >
      <View style={styles.overlay}>
        <View style={[styles.box, { backgroundColor: colors.card }]}>
          {/* Header Indicator: Visual cue that matches the action type (e.g., destructive red) */}
          <View style={[styles.indicator, { backgroundColor: accentColor }]} />

          {/* Optional Close Icon (X) in the top right */}
          {onClose && (
            <TouchableOpacity
              style={[styles.closeButton, { backgroundColor: colors.paper2 }]}
              onPress={onClose}
              hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
            >
              <Ionicons name="x" size={24} color={colors.ink4} />
            </TouchableOpacity>
          )}

          <View style={styles.content}>
            <Text style={[styles.title, { color: colors.ink }]}>{title}</Text>
            <Text style={[styles.message, { color: colors.ink2 }]}>{message}</Text>
            {children}
          </View>

          <View style={[styles.footer, { backgroundColor: colors.paper2 }]}>
            {/* Cancel Action: Designed as a secondary button with subtle slate tones */}
            <Pressable
              style={({ pressed }) => [
                styles.btn,
                styles.cancelBtn,
                { backgroundColor: colors.paper3 },
                // Micro-interactions for tactile feedback
                pressed && styles.btnPressed
              ]}
              onPress={onCancel}
              disabled={busy}
            >
              <Text numberOfLines={1} style={[styles.cancelText, { color: colors.ink2 }]}>{cancelText}</Text>
            </Pressable>

            {/* Confirm Action: Themed with confirmColor to emphasize primary intent */}
            <Pressable
              style={({ pressed }) => [
                styles.btn,
                { backgroundColor: accentColor, opacity: busy ? 0.7 : 1 },
                pressed && styles.btnPressed
              ]}
              onPress={onConfirm}
              disabled={busy}
            >
              <Text numberOfLines={1} style={[styles.confirmText, { color: colors.onAccent }]}>{busy ? 'Working…' : confirmText}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(15, 23, 42, 0.75)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  box: {
    width: Math.min(width * 0.80, 320),
    borderRadius: 20,
    overflow: "hidden",
    ...Platform.select({
      ios: {
        shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    ...Platform.select({ web: { boxShadow: '0 8px 16px rgba(0,0,0,0.18)' } as any }),
      },
      android: {
        elevation: 10,
      },
    }),
  },
  indicator: {
    height: 4,
    width: "100%",
    opacity: 0.85,
  },
  content: {
    paddingTop: 20,
    paddingHorizontal: 18,
    paddingBottom: 16,
    alignItems: "center",
  },
  title: {
    fontSize: 17,
    fontWeight: "800",
    fontFamily: FontFamily.sans,
    textAlign: "center",
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  message: {
    fontSize: 13,
    fontFamily: FontFamily.sans,
    textAlign: "center",
    lineHeight: 19,
  },
  footer: {
    flexDirection: "row",
    padding: 14,
    gap: 10,
  },
  btn: {
    flex: 1,
    height: 42,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  btnPressed: {
    opacity: 0.8,
    // Native-like scale reduction during press event
    transform: [{ scale: 0.97 }],
  },
  cancelBtn: {
  },
  cancelText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.sans,
  },
  confirmText: {
    fontSize: 14,
    fontWeight: "700",
    fontFamily: FontFamily.sans,
  },
  closeButton: {
    position: "absolute",
    top: 15,
    right: 15,
    zIndex: 10,
    borderRadius: 20,
    width: 32,
    height: 32,
    justifyContent: "center",
    alignItems: "center",
  },
});
